import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

const CATEGORIES = [
  "Dairy", "Cheese", "Meat", "Fish", "Oysters",
  "Fruits", "Vegetables", "Spices", "Spirits", "Dry Stock"
];

// --- Helpers ---

function validateItemForm(formData) {
  if (!formData.name.trim()) return 'Item name is required';
  if (!formData.category) return 'Category is required';
  if (!formData.quantity || parseFloat(formData.quantity) < 0) return 'Valid quantity is required';
  return null;
}

function buildItemPayload(formData) {
  return {
    name: formData.name.trim(),
    category: formData.category,
    quantity: parseFloat(formData.quantity),
    unit: formData.unit,
    low_stock_threshold: parseFloat(formData.low_stock_threshold) || 5,
    expiry_date: formData.expiry_date ? formData.expiry_date.toISOString() : null,
  };
}

function getSubmitLabel(loading, isEdit) {
  if (loading) return 'Saving...';
  return isEdit ? 'Update' : 'Add Item';
}

const INITIAL_FORM = {
  name: '',
  category: '',
  quantity: '',
  unit: 'units',
  low_stock_threshold: '5',
  expiry_date: null,
};

function formDataFromItem(item) {
  if (!item) return INITIAL_FORM;
  return {
    name: item.name || '',
    category: item.category || '',
    quantity: item.quantity?.toString() || '',
    unit: item.unit || 'units',
    low_stock_threshold: item.low_stock_threshold?.toString() || '5',
    expiry_date: item.expiry_date ? new Date(item.expiry_date) : null,
  };
}

// --- Main component ---

export const AddItemModal = ({ open, onClose, onSave, item = null }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setFormData(formDataFromItem(item));
    setError('');
  }, [item, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateItemForm(formData);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      await onSave(buildItemPayload(formData));
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="sm:max-w-[500px] bg-white" data-testid="add-item-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#0A192F]">
            {item ? 'Edit Item' : 'Add New Item'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 text-sm border border-red-200" data-testid="form-error">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name" className="text-xs uppercase tracking-wider text-[#475569] font-bold">
              Item Name
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Whole Milk"
              className="border-[#E2E8F0] focus:border-[#0A192F] focus:ring-[#0A192F]"
              data-testid="item-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-[#475569] font-bold">
              Category
            </Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger className="border-[#E2E8F0]" data-testid="category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat} data-testid={`category-${cat.toLowerCase()}`}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity" className="text-xs uppercase tracking-wider text-[#475569] font-bold">
                Quantity
              </Label>
              <Input
                id="quantity"
                type="number"
                min="0"
                step="0.1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                placeholder="0"
                className="border-[#E2E8F0] focus:border-[#0A192F] focus:ring-[#0A192F]"
                data-testid="quantity-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit" className="text-xs uppercase tracking-wider text-[#475569] font-bold">
                Unit
              </Label>
              <Select
                value={formData.unit}
                onValueChange={(value) => setFormData({ ...formData, unit: value })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="units">Units</SelectItem>
                  <SelectItem value="kg">Kilograms</SelectItem>
                  <SelectItem value="g">Grams</SelectItem>
                  <SelectItem value="lb">Pounds</SelectItem>
                  <SelectItem value="oz">Ounces</SelectItem>
                  <SelectItem value="L">Liters</SelectItem>
                  <SelectItem value="ml">Milliliters</SelectItem>
                  <SelectItem value="bottles">Bottles</SelectItem>
                  <SelectItem value="packs">Packs</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="threshold" className="text-xs uppercase tracking-wider text-[#475569] font-bold">
              Low Stock Threshold
            </Label>
            <Input
              id="threshold"
              type="number"
              min="0"
              step="1"
              value={formData.low_stock_threshold}
              onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
              placeholder="5"
              className="border-[#E2E8F0] focus:border-[#0A192F] focus:ring-[#0A192F]"
              data-testid="threshold-input"
            />
            <p className="text-xs text-[#475569]">Alert when quantity falls below this number</p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-[#475569] font-bold">
              Expiry Date (Optional)
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal border-[#E2E8F0]"
                  data-testid="expiry-date-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.expiry_date ? format(formData.expiry_date, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  selected={formData.expiry_date}
                  onSelect={(date) => setFormData({ ...formData, expiry_date: date })}
                  initialFocus
                  data-testid="expiry-calendar"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[#0A192F] text-[#0A192F] hover:bg-slate-100 uppercase tracking-wide text-sm font-bold"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#0A192F] text-white hover:bg-[#0F294D] uppercase tracking-wide text-sm font-bold"
              data-testid="save-item-btn"
            >
              {getSubmitLabel(loading, !!item)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
